/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { SEC_TO_MS_CONVERSION } from './DateTimeUtils';
import {
  SPACE_VEHICLE_STATUS,
  TimelineChartData,
} from '../components/isanPortal/transportMonitoring/FullSpaceVehicleView/charts/performance/HorizontalTimeline';
import { ChartData } from '../components/isanPortal/transportMonitoring/FullSpaceVehicleView/charts/performance/LinkAvailability';
import TerminalHistory from '../shared/entities/terminalHistory';
import { isNotNullOrUndefined } from './NullUtils';
import appTheme from '../components/common/theme/appTheme.json';
import { NetworkAvailability, SpaceNetworkInfo } from '../shared/entities/terminal';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export const TIMELINE_TICK_INTERVALE_IN_MINUTES = 30;

export const getTimelineChartData = (terminalHistory: { [key: number]: TerminalHistory }) => {
  let timelineChartData: TimelineChartData[] = [];

  //ascending order
  const sortedTerminalHistoryValues: { [key: number]: TerminalHistory } = Object.values(terminalHistory).sort(
    (a, b) => a.sourceTime - b.sourceTime,
  );

  Object.values(sortedTerminalHistoryValues).forEach(history => {
    timelineChartData.push({
      x: 0,
      y: history.sourceTime * SEC_TO_MS_CONVERSION,
      status: history.connectivityStatus === 'active' ? SPACE_VEHICLE_STATUS.ACTIVE : SPACE_VEHICLE_STATUS.IN_ACTIVE,
    });
  });

  return timelineChartData;
};

//tick values in 30 mins interval
export const getTimelineTickValues = (chartData: TimelineChartData[]) => {
  const size = chartData.length;
  const firstTickValue = chartData[0].y;
  const lastTickValue = chartData[size - 1].y;

  let tickValues = [firstTickValue];

  let index = 1;
  while (index < chartData.length - 1) {
    const currTimestamp = chartData[index].y;
    const prevTimestamp = tickValues[tickValues.length - 1];
    const diffInMin = (new Date(currTimestamp).getTime() - new Date(prevTimestamp).getTime()) / 60000;

    if (diffInMin >= TIMELINE_TICK_INTERVALE_IN_MINUTES) {
      tickValues.push(currTimestamp);
    }
    index += 1;
  }
  const diffInMin = (new Date(lastTickValue).getTime() - new Date(tickValues.length - 1).getTime()) / 60000;
  if (diffInMin >= TIMELINE_TICK_INTERVALE_IN_MINUTES) {
    tickValues.push(lastTickValue);
  }

  return tickValues;
};

export const getSlaChartData = (terminalHistory: { [key: number]: TerminalHistory }) => {
  //ascending order
  const sortedTerminalHistoryValues: { [key: number]: TerminalHistory } = Object.values(terminalHistory).sort(
    (a, b) => a.sourceTime - b.sourceTime,
  );

  let slaChartData: ChartData[] = [];

  Object.values(sortedTerminalHistoryValues).forEach(history => {
    if (isNotNullOrUndefined(history.sourceTime) && isNotNullOrUndefined(history.flSlaLowerBoundBps)) {
      slaChartData.push({
        x: history.sourceTime * SEC_TO_MS_CONVERSION,
        y: history.flSlaLowerBoundBps, //TODO: replace history.flSlaLowerBoundBps with space vehicle sla later
      });
    }
  });

  return slaChartData;
};

export const getLinkAvailabilityChartData = (terminalHistory: { [key: number]: TerminalHistory }) => {
  //ascending order
  const sortedTerminalHistoryValues: { [key: number]: TerminalHistory } = Object.values(terminalHistory).sort(
    (a, b) => a.sourceTime - b.sourceTime,
  );

  let slaChartData: ChartData[] = [];

  Object.values(sortedTerminalHistoryValues).forEach(history => {
    if (isNotNullOrUndefined(history.sourceTime) && isNotNullOrUndefined(history.flDataRateBps)) {
      slaChartData.push({
        x: history.sourceTime * SEC_TO_MS_CONVERSION,
        y: history.flDataRateBps!, //TODO: replace history.flDataRateBps with linkavailibity data later
      });
    }
  });

  return slaChartData;
};

export const getSvConnectivityColor = (svConnectivityStatus: string | undefined) => {
  if (svConnectivityStatus === NetworkAvailability.AVAILABLE) {
    return appTheme.svNetworkIsAvailableTextColor;
  }
  if (svConnectivityStatus === NetworkAvailability.UNAVAILABLE) {
    return appTheme.svNetworkIsUnavailableTextColor;
  }

  return appTheme.svNetworkIsOfflineTextColor;
};

export const getSvConnectivityIcon = (svConnectivityStatus: string, size = 12) => {
  const commonStyle = {
    fontSize: `${size}px`,
    width: `${size}px`,
    height: `${size}px`,
    color: getSvConnectivityColor(svConnectivityStatus),
  };

  return (
    <FiberManualRecordIcon
      data-test="connectivity-icon"
      style={{ ...commonStyle }}
      stroke={commonStyle.color}
      strokeWidth={4}
    />
  );
};

export const getSvNetworkAvailabilityText = (spaceNetworkInfo: SpaceNetworkInfo) => {
  if (spaceNetworkInfo.networkAvailability === NetworkAvailability.AVAILABLE) {
    return 'Active';
  } else if (spaceNetworkInfo.networkAvailability === NetworkAvailability.UNAVAILABLE) {
    return 'Intermittient';
  } else {
    return 'Disconnected';
  }
};
